import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { todayStr, TX_META, curSym, inr, fmtDate } from "../lib/format";
import { SheetPicker, MultiSheetPicker } from "../components/SheetPicker";

const TYPES = ["expense", "card_purchase", "income", "card_repayment", "transfer", "refund"];
const emptyLine = () => ({ amount: "", category_id: null, tag_ids: [], business_id: null, project_id: null, alloc_amount: "", notes: "" });

export default function AddTransaction() {
  const navigate = useNavigate();
  const [lookups, setLookups] = useState(null);
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [sourceId, setSourceId] = useState(null);
  const [destId, setDestId] = useState(null);
  const [merchant, setMerchant] = useState("");
  const [description, setDescription] = useState("");
  const [memberId, setMemberId] = useState(null);
  const [incomeSourceId, setIncomeSourceId] = useState(null);
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);

  useEffect(() => {
    api.get("/lookups").then((r) => setLookups(r.data)).catch(() => {});
  }, []);

  const accounts = lookups?.accounts || [];
  const cards = accounts.filter((a) => a.account_type === "credit_card");
  const nonCards = accounts.filter((a) => a.account_type !== "credit_card");
  const memberNameById = Object.fromEntries((lookups?.members || []).map((m) => [m.id, m.display_name]));
  const accOpt = (list) => list.map((a) => ({
    id: a.id, label: a.name,
    sub: [a.account_type.replace("_", " "), memberNameById[a.owner_member_id]].filter(Boolean).join(" · "),
  }));

  const catOptions = useMemo(() => {
    const kind = type === "income" ? "income" : "expense";
    return (lookups?.categories || []).filter((c) => c.category_kind === kind).map((c) => ({ id: c.id, label: c.name }));
  }, [lookups, type]);
  const tagOptions = (lookups?.tags || []).map((t) => ({ id: t.id, label: t.name }));
  const bizOptions = (lookups?.businesses || []).map((b) => ({ id: b.id, label: b.name }));
  const memberOptions = (lookups?.members || []).map((m) => ({ id: m.id, label: m.display_name }));
  const incomeSrcOptions = (lookups?.income_sources || []).map((s) => ({ id: s.id, label: s.name }));
  const projectOptions = (bizId) => (lookups?.projects || []).filter((p) => p.business_id === bizId).map((p) => ({ id: p.id, label: p.name }));

  const hasLines = type === "expense" || type === "card_purchase";
  const singleLine = lines.length === 1;

  const updateLine = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const save = async (post, allowDuplicate = false) => {
    const total = parseFloat(amount);
    if (!total || total <= 0) return toast.error("Enter a valid amount");
    const payload = {
      transaction_type: type,
      transaction_date: date,
      total_amount: total,
      source_account_id: sourceId,
      destination_account_id: destId,
      earning_member_id: type === "income" ? memberId : null,
      income_source_id: type === "income" ? incomeSourceId : null,
      merchant: merchant || null,
      description: description || null,
      post,
      allow_duplicate: allowDuplicate,
      lines: hasLines
        ? lines.map((l) => ({
            amount: singleLine ? total : parseFloat(l.amount || 0),
            category_id: l.category_id,
            tag_ids: l.tag_ids,
            notes: l.notes || null,
            allocations: l.business_id
              ? [{ business_id: l.business_id, project_id: l.project_id, amount: parseFloat(l.alloc_amount) || (singleLine ? total : parseFloat(l.amount || 0)) }]
              : [],
          }))
        : [],
    };
    setBusy(true);
    try {
      await api.post("/transactions", payload);
      toast.success(post ? "Transaction posted" : "Draft saved");
      navigate("/transactions");
    } catch (e) {
      if (e.response?.status === 409) {
        setDupWarning({ ...e.response.data.detail, post });
      } else {
        toast.error(e.response?.data?.detail || "Could not save");
      }
    } finally {
      setBusy(false);
    }
  };

  const label = "text-xs uppercase tracking-wider font-semibold text-muted-foreground";
  const input = "w-full h-12 px-4 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="min-h-screen pb-32" data-testid="add-transaction-page">
      <div className="sticky top-0 z-30 bg-background/90 backdrop-blur-lg px-5 pt-5 pb-3 flex items-center justify-between">
        <h1 className="text-xl font-heading font-bold">New transaction</h1>
        <button data-testid="add-tx-close" onClick={() => navigate(-1)} className="h-11 w-11 flex items-center justify-center rounded-xl active:bg-secondary"><X size={20} /></button>
      </div>

      <div className="px-5 space-y-5">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5">
          {TYPES.map((t) => (
            <button key={t} data-testid={`tx-type-${t}`} onClick={() => { setType(t); setSourceId(null); setDestId(null); setLines([emptyLine()]); }}
              className={`shrink-0 h-11 px-4 rounded-full text-sm font-medium border transition-colors ${type === t ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-muted-foreground"}`}>
              {TX_META[t].label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-border rounded-2xl p-4">
          <label className={label}>Amount ({curSym()})</label>
          <input data-testid="tx-amount-input" type="number" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full mt-1 text-4xl font-light tracking-tight bg-transparent focus:outline-none tnum placeholder:text-muted-foreground/40" />
        </div>

        <div>
          <label className={label}>Date</label>
          <input data-testid="tx-date-input" type="date" className={input + " mt-1"} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        {(type === "expense") && (
          <div><label className={label}>Paid from</label><div className="mt-1"><SheetPicker testId="tx-source-picker" title="Paid from" options={accOpt(nonCards)} value={sourceId} onChange={setSourceId} placeholder="Select account" /></div></div>
        )}
        {(type === "card_purchase") && (
          <div><label className={label}>Credit card</label><div className="mt-1"><SheetPicker testId="tx-source-picker" title="Credit card" options={accOpt(cards)} value={sourceId} onChange={setSourceId} placeholder="Select card" /></div></div>
        )}
        {(type === "income" || type === "refund") && (
          <div><label className={label}>Received into</label><div className="mt-1"><SheetPicker testId="tx-dest-picker" title="Received into" options={accOpt(accounts)} value={destId} onChange={setDestId} placeholder="Select account" /></div></div>
        )}
        {type === "card_repayment" && (
          <>
            <div><label className={label}>Pay from</label><div className="mt-1"><SheetPicker testId="tx-source-picker" title="Pay from" options={accOpt(nonCards)} value={sourceId} onChange={setSourceId} placeholder="Select account" /></div></div>
            <div><label className={label}>Credit card</label><div className="mt-1"><SheetPicker testId="tx-dest-picker" title="Credit card" options={accOpt(cards)} value={destId} onChange={setDestId} placeholder="Select card" /></div></div>
          </>
        )}
        {type === "transfer" && (
          <>
            <div><label className={label}>From</label><div className="mt-1"><SheetPicker testId="tx-source-picker" title="From account" options={accOpt(accounts)} value={sourceId} onChange={setSourceId} placeholder="Select account" /></div></div>
            <div><label className={label}>To</label><div className="mt-1"><SheetPicker testId="tx-dest-picker" title="To account" options={accOpt(accounts.filter((a) => a.id !== sourceId))} value={destId} onChange={setDestId} placeholder="Select account" /></div></div>
          </>
        )}

        {type === "income" && (
          <>
            <div><label className={label}>Earning member</label><div className="mt-1"><SheetPicker testId="tx-member-picker" title="Earning member" options={memberOptions} value={memberId} onChange={setMemberId} placeholder="Who earned this?" /></div></div>
            <div><label className={label}>Income source (optional)</label><div className="mt-1"><SheetPicker testId="tx-income-source-picker" title="Income source" options={incomeSrcOptions} value={incomeSourceId} onChange={setIncomeSourceId} placeholder="Select source" /></div></div>
          </>
        )}

        <div>
          <label className={label}>{type === "income" ? "Payer" : "Merchant / payee"} (optional)</label>
          <input data-testid="tx-merchant-input" className={input + " mt-1"} placeholder="e.g. Big Bazaar" value={merchant} onChange={(e) => setMerchant(e.target.value)} />
        </div>

        {hasLines && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className={label}>{singleLine ? "Category & details" : "Split lines"}</label>
              <button data-testid="tx-add-split" onClick={() => setLines([...lines, emptyLine()])} className="text-sm font-medium flex items-center gap-1 text-transfer"><Plus size={14} /> Split</button>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-4 space-y-3">
                {!singleLine && (
                  <div className="flex items-center gap-2">
                    <input data-testid={`tx-line-amount-${i}`} type="number" inputMode="decimal" placeholder="Line amount" value={l.amount} onChange={(e) => updateLine(i, { amount: e.target.value })} className="flex-1 h-11 px-3 rounded-xl bg-background border border-border tnum" />
                    <button data-testid={`tx-line-remove-${i}`} onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="h-11 w-11 flex items-center justify-center rounded-xl text-destructive active:bg-expensebg"><Trash size={18} /></button>
                  </div>
                )}
                <SheetPicker testId={`tx-line-category-${i}`} title="Category" options={catOptions} value={l.category_id} onChange={(v) => updateLine(i, { category_id: v })} placeholder="Select category" />
                <MultiSheetPicker testId={`tx-line-tags-${i}`} title="Tags" options={tagOptions} values={l.tag_ids} onChange={(v) => updateLine(i, { tag_ids: v })} placeholder="Tags (optional)" />
                <SheetPicker testId={`tx-line-business-${i}`} title="Business" options={[{ id: null, label: "None" }, ...bizOptions]} value={l.business_id} onChange={(v) => updateLine(i, { business_id: v, project_id: null })} placeholder="Business (optional)" />
                {l.business_id && projectOptions(l.business_id).length > 0 && (
                  <SheetPicker testId={`tx-line-project-${i}`} title="Project" options={[{ id: null, label: "None" }, ...projectOptions(l.business_id)]} value={l.project_id} onChange={(v) => updateLine(i, { project_id: v })} placeholder="Project (optional)" />
                )}
                <input data-testid={`tx-line-notes-${i}`} className="w-full h-11 px-3 rounded-xl bg-background border border-border text-sm" placeholder="Line note (optional)" value={l.notes} onChange={(e) => updateLine(i, { notes: e.target.value })} />
              </div>
            ))}
          </div>
        )}

        <div>
          <label className={label}>Notes (optional)</label>
          <textarea data-testid="tx-notes-input" className="w-full mt-1 p-4 rounded-xl bg-white border border-border min-h-[70px]" placeholder="Anything to remember…" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      {dupWarning && (
        <div className="fixed inset-0 z-50 bg-foreground/40 flex items-end justify-center" data-testid="duplicate-warning">
          <div className="w-full max-w-md bg-white rounded-t-3xl p-5 pb-8 safe-bottom">
            <h2 className="text-lg font-heading font-semibold">Possible duplicate</h2>
            <p className="mt-2 text-sm text-muted-foreground">{dupWarning.message}</p>
            {dupWarning.duplicate && (
              <div className="mt-3 bg-background border border-border rounded-xl px-4 py-3 text-sm">
                <p className="font-medium">{dupWarning.duplicate.merchant || dupWarning.duplicate.description || TX_META[dupWarning.duplicate.transaction_type]?.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fmtDate(dupWarning.duplicate.transaction_date)} · {inr(dupWarning.duplicate.total_amount)} · {dupWarning.duplicate.status}
                </p>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button data-testid="dup-cancel-btn" onClick={() => setDupWarning(null)} className="flex-1 h-12 rounded-xl border border-border font-medium">Cancel</button>
              <button data-testid="dup-save-anyway-btn" onClick={() => { const p = dupWarning.post; setDupWarning(null); save(p, true); }} className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-medium">Save anyway</button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white/90 backdrop-blur-xl border-t border-border p-4 safe-bottom flex gap-2">
        <button data-testid="tx-save-draft-btn" disabled={busy} onClick={() => save(false)} className="flex-1 h-12 rounded-xl border border-primary font-medium active:scale-[0.98] transition-transform disabled:opacity-60">Save draft</button>
        <button data-testid="tx-post-btn" disabled={busy} onClick={() => save(true)} className="flex-[2] h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform disabled:opacity-60">{busy ? "Saving…" : "Post"}</button>
      </div>
    </div>
  );
}
